import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { applyBrandColor } from "@/lib/brand";
import { payOf } from "@/lib/payPeriod";
import { setTimeZone } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const CACHE_KEY = "cj_company";
const CompanyContext = createContext({ company: null, setCompany: () => {}, refresh: async () => {} });

const cached = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; }
};

// The signed-in user's company (their supervisor's name, logo and colour).
export const CompanyProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [company, setCompanyState] = useState(() => { const c = cached(); setTimeZone(c?.pay?.timezone); return c; });

  const setCompany = useCallback((c) => {
    setTimeZone(c?.pay?.timezone);
    setCompanyState(c);
    applyBrandColor(c?.color);
    if (c) localStorage.setItem(CACHE_KEY, JSON.stringify(c));
    else localStorage.removeItem(CACHE_KEY);
  }, []);

  const refresh = useCallback(async () => {
    try { setCompany(await api.company()); } catch { /* keep the current brand */ }
  }, [setCompany]);

  useEffect(() => { applyBrandColor(cached()?.color); }, []);

  useEffect(() => {
    window.addEventListener("cj-billing-locked", refresh);
    return () => window.removeEventListener("cj-billing-locked", refresh);
  }, [refresh]);

  useEffect(() => {
    if (user?.id) refresh();
    else if (!loading) setCompany(null);
  }, [user?.id, loading, refresh, setCompany]);

  return (
    <CompanyContext.Provider value={{ company, setCompany, refresh }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => useContext(CompanyContext);
export const usePay = () => payOf(useContext(CompanyContext).company);
